import { Wrench } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import type { ProductionBatchIngredient } from "~/types/production";
import { formatCurrency } from "~/utils/helpers";
import { formatUsd } from "~/utils/oilConversions";

interface BatchIngredientsTableProps {
  ingredients: ProductionBatchIngredient[];
  showActual?: boolean;
  /**
   * Real costed unit cost / total (WAC from costing_movements) keyed by
   * ingredient id. When present for a row, it replaces the batch-creation
   * snapshot so the Receta reconciles with the Consumos WAC page / real COGS.
   */
  costsById?: Map<string, { unitCostClp: number; totalClp: number }>;
  /**
   * When provided (completed batch + privileged role), renders a per-row
   * "Corregir" action that opens the consumption-correction dialog.
   */
  onCorrect?: (ingredient: ProductionBatchIngredient) => void;
}

export function BatchIngredientsTable({
  ingredients,
  showActual = false,
  costsById,
  onCorrect,
}: BatchIngredientsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Ingrediente</TableHead>
          <TableHead>Tipo</TableHead>
          <TableHead className="text-right">Cantidad Planificada</TableHead>
          {showActual && (
            <TableHead className="text-right">Cantidad Real</TableHead>
          )}
          <TableHead className="text-right">Costo Unit.</TableHead>
          <TableHead className="text-right">Costo Total</TableHead>
          <TableHead>Estado</TableHead>
          {onCorrect && <TableHead className="w-[50px]" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {ingredients.map((ingredient, index) => {
          const isOil = !!ingredient.oil_type_id;
          const code = isOil
            ? ingredient.oil_type?.code
            : ingredient.raw_material?.code;
          const name = isOil
            ? ingredient.oil_type?.name
            : ingredient.raw_material?.name;

          const unit = isOil ? "kg" : ingredient.raw_material?.unit || "units";

          // planned_quantity is already stored in the final unit (kg for oils, units for materials)
          const quantity = showActual
            ? (ingredient.actual_quantity ?? ingredient.planned_quantity)
            : ingredient.planned_quantity;

          // Prefer the real costed WAC (from costing_movements) when available;
          // fall back to the batch-creation snapshot for uncosted batches.
          // unit_cost_clp is per kg for oils, per unit for materials.
          const costed = costsById?.get(ingredient.id);
          const displayUnitCostClp = costed?.unitCostClp ?? ingredient.unit_cost_clp;
          const totalCost = costed?.totalClp ?? ingredient.unit_cost_clp * quantity;

          return (
            <TableRow
              key={ingredient.id}
              className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
              style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
            >
              <TableCell className="font-medium">
                {code && (
                  <div className="text-xs font-normal text-muted-foreground">
                    {code}
                  </div>
                )}
                <div>{name || "-"}</div>
              </TableCell>
              <TableCell>
                <Badge variant={isOil ? "default" : "secondary"}>
                  {isOil ? "Aceite" : "Material"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {quantity.toFixed(2)} {unit}
              </TableCell>
              {showActual && (
                <TableCell className="text-right">
                  {ingredient.actual_quantity !== null
                    ? `${ingredient.actual_quantity.toFixed(2)} ${unit}`
                    : "-"}
                </TableCell>
              )}
              <TableCell className="text-right">
                {costed
                  ? `$${formatCurrency(displayUnitCostClp)}/${unit}`
                  : isOil && ingredient.unit_cost_usd
                    ? `${formatUsd(ingredient.unit_cost_usd)}/kg`
                    : `$${formatCurrency(ingredient.unit_cost_clp)}/${unit}`}
              </TableCell>
              <TableCell className="text-right font-medium">
                ${formatCurrency(totalCost)}
              </TableCell>
              <TableCell>
                {ingredient.is_override ? (
                  <Badge variant="outline">Modificado</Badge>
                ) : (
                  <Badge variant="secondary">Original</Badge>
                )}
              </TableCell>
              {onCorrect && (
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title="Corregir consumo"
                    onClick={() => onCorrect(ingredient)}
                  >
                    <Wrench className="h-4 w-4" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
